/**
 * PaymentFlow — the flow section of the payment detail page, laid out as the
 * payment travels: the outbound lane, the wait at the bank, then the inbound
 * lane holding one diagram per bank response type. The bank is time spent
 * rather than a system we observe, so it sits between the lanes with an arrow
 * in and an arrow out; the idle gap between two response types reads the same
 * way.
 *
 * The detail panel sits beside the section, not inside a diagram, so the
 * selection is held here: one panel, filled by a system picked anywhere.
 * Redux-connected; renders nothing without graphs.
 */
import { CSSProperties, Fragment, PointerEvent as ReactPointerEvent, useRef, useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import { useAppSelector } from 'src/store/hooks';
import { FlowGraph, FlowNode } from 'src/store/slices/paymentDetail/paymentDetailTypes';
import { FlowDiagram } from './FlowDiagram';
import { FlowNodeDetails } from './FlowNodeDetails';
import './flow.css';

const Chips = ({ bottleneck, latency }: { bottleneck?: string; latency?: string }) => (
  <span className="flow-chips">
    {bottleneck && (
      <span className="flow-chip flow-chip--warning">
        <span aria-hidden="true">⚠</span> Slowest: {bottleneck}
      </span>
    )}
    {latency && <span className="flow-chip">{latency}</span>}
  </span>
);

interface GraphProps {
  graph: FlowGraph;
  caption?: string;
  card?: boolean;
  selectedNodeId?: string;
  onSelectNode: (nodeId: string) => void;
}

/** One graph. The hint stands down once this graph holds the selection. */
const Graph = ({ graph, caption, card, selectedNodeId, onSelectNode }: GraphProps) => {
  const hasSelection = graph.nodes.some(node => node.id === selectedNodeId);

  return (
    <section
      className={card ? 'flow-block flow-block--card' : 'flow-block'}
      aria-label={`${caption ?? graph.title} flow`}
    >
      <header className="flow-block__header">
        {caption && <span className="flow-block__caption">{caption}</span>}
        <Chips bottleneck={graph.bottleneckLabel} latency={graph.totalLatency} />
      </header>

      <div className="flow-block__canvas">
        <FlowDiagram graph={graph} selectedNodeId={selectedNodeId} onSelectNode={onSelectNode} />
        {!hasSelection && <p className="flow-block__hint">Select a system to see its phases.</p>}
      </div>

      {graph.dropped.length > 0 && (
        <details className="flow-dropped">
          <summary>{graph.dropped.length} event(s) not placed on this diagram</summary>
          <ul>
            {graph.dropped.map(event => (
              <li key={event.id}>{event.system} · {event.status} · {event.at}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
};

/** Time between two blocks. Label is optional: a response-type gap is just a duration. */
const Transit = ({ label, value }: { label?: string; value: string }) => (
  <div className="flow-transit">
    <span className="flow-transit__arrow" aria-hidden="true" />
    <div className="flow-transit__box">
      {label && <span className="flow-transit__label">{label}</span>}
      <span className="flow-transit__value">{value}</span>
    </div>
    <span className="flow-transit__arrow" aria-hidden="true" />
  </div>
);

interface LaneProps {
  label: string;
  graphs: FlowGraph[];
  selectedNodeId?: string;
  onSelectNode: (nodeId: string) => void;
}

const Lane = ({ label, graphs, selectedNodeId, onSelectNode }: LaneProps) => {
  // Several response types in one lane each get a caption and their own card,
  // so the gap between them reads as a step from one box to the next.
  const several = graphs.length > 1;

  return (
    <section className={`flow-lane flow-lane--${label.toLowerCase()}`} aria-label={`${label} flow`}>
      <h3 className="flow-lane__label">{label}</h3>
      {graphs.map(graph => (
        <Fragment key={graph.id}>
          {graph.gapFromPrevious && <Transit value={graph.gapFromPrevious} />}
          <Graph
            graph={graph}
            caption={several ? graph.title : undefined}
            card={several}
            selectedNodeId={selectedNodeId}
            onSelectNode={onSelectNode}
          />
        </Fragment>
      ))}
    </section>
  );
};

/** Bounds the splitter both ways: neither column is readable below this. */
const COLUMN_MIN_PX = 240;
/** One arrow key press, in px. */
const NUDGE_PX = 24;

export const PaymentFlow = () => {
  const detail = useAppSelector(state => state.paymentDetail.data);
  const [selectedNodeId, setSelectedNodeId] = useState<string | undefined>();
  const [detailShare, setDetailShare] = useState(50);
  const splitRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  /** px to a clamped share, held as a share so it survives a window resize. */
  const resizeTo = (widthPx: number) => {
    const total = splitRef.current?.clientWidth ?? 0;
    if (total <= 0) return;
    // Measured against the live width, so the bound is not a guessed percent.
    const min = (COLUMN_MIN_PX / total) * 100;
    const share = (widthPx / total) * 100;
    setDetailShare(Math.min(Math.max(share, min), Math.max(min, 100 - min)));
  };

  const detailWidthPx = () => ((splitRef.current?.clientWidth ?? 0) * detailShare) / 100;

  // Captured, so a drag keeps tracking once the pointer leaves the handle.
  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = { startX: event.clientX, startWidth: detailWidthPx() };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    // Guarded on the button too: a cancelled gesture can leave the ref set,
    // and a later hover must not resize.
    if (!drag || event.buttons === 0) return;
    // Dragging left widens the detail, since it is the right-hand column.
    resizeTo(drag.startWidth - (event.clientX - drag.startX));
  };

  if (!detail) return null;

  const { outbound, inbound, bankWait } = detail.flow;
  if (outbound.length === 0 && inbound.length === 0) return null;

  // Node ids are unique across graphs, so one id addresses the section.
  // concat over flatMap: the TS lib here predates it.
  const selectedNode = outbound.concat(inbound)
    .reduce<FlowNode[]>((nodes, graph) => nodes.concat(graph.nodes), [])
    .find(node => node.id === selectedNodeId);

  const toggleNode = (nodeId: string) =>
    setSelectedNodeId(current => (current === nodeId ? undefined : nodeId));

  return (
    <div className="flow-root">
      <Container
        header={
          <Header
            variant="h2"
            actions={<Chips
              bottleneck={detail.bottleneckLabel}
              latency={detail.totalLatency && `${detail.totalLatency} end to end`}
            />}
          >
            Flow
          </Header>
        }
      >
        <div className="flow-split" ref={splitRef}>
          <div className="flow">
            {outbound.length > 0 && (
              <Lane
                label="Outbound"
                graphs={outbound}
                selectedNodeId={selectedNodeId}
                onSelectNode={toggleNode}
              />
            )}
            {bankWait && outbound.length > 0 && inbound.length > 0 && (
              <Transit label="Waiting for bank" value={bankWait} />
            )}
            {inbound.length > 0 && (
              <Lane
                label="Inbound"
                graphs={inbound}
                selectedNodeId={selectedNodeId}
                onSelectNode={toggleNode}
              />
            )}
          </div>

          {selectedNode && (
            <Fragment>
              <div
                className="flow-split__handle"
                role="separator"
                tabIndex={0}
                aria-orientation="vertical"
                aria-label="Resize the detail panel"
                aria-valuenow={Math.round(detailShare)}
                aria-valuemin={0}
                aria-valuemax={100}
                onPointerDown={startDrag}
                onPointerMove={onDrag}
                /* Covers pointerup and pointercancel alike. */
                onLostPointerCapture={() => { dragRef.current = null; }}
                onKeyDown={event => {
                  const step = event.key === 'ArrowLeft' ? NUDGE_PX
                    : event.key === 'ArrowRight' ? -NUDGE_PX : 0;
                  if (step === 0) return;
                  event.preventDefault();
                  resizeTo(detailWidthPx() + step);
                }}
              />
              <aside
                className="flow-split__detail"
                aria-label={`${selectedNode.system} detail`}
                style={{ '--flow-detail-share': `${detailShare}%` } as CSSProperties}
              >
                <FlowNodeDetails node={selectedNode} onClose={() => setSelectedNodeId(undefined)} />
              </aside>
            </Fragment>
          )}
        </div>
      </Container>
    </div>
  );
};
